import { useQuery } from '@tanstack/react-query';
import type {
  CartonDto,
  OrderDetailDto,
  OrderLineDto,
  PickMethod,
  PickTaskDto,
} from '@zenarty/shared';
import {
  ArrowLeft,
  Ban,
  Boxes,
  CheckCircle2,
  FileText,
  PackageCheck,
  PauseCircle,
  Play,
  PlayCircle,
  Printer,
  Send,
  Undo2,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api } from '../api';
import { useAuth } from '../auth';
import { Barcode, printElement } from '../components/barcode';
import {
  Badge,
  Button,
  Callout,
  Card,
  EmptyState,
  ErrorMessage,
  Field,
  KeyValue,
  PageHeader,
  Skeleton,
} from '../components/kit';
import { Dialog } from '../components/overlays';
import { DataTable, type Column } from '../components/table';
import { formatDate, formatDateTime, formatNumber, timeAgo } from '../lib/format';
import { useAction } from '../lib/queries';
import { Progress } from './Inbound';
import { cutoffLabel, OrderStatusBadge, RiskBadge } from './Orders';

const STEPS: { key: keyof OrderDetailDto; label: string }[] = [
  { key: 'createdAt', label: 'Received' },
  { key: 'allocatedAt', label: 'Allocated' },
  { key: 'releasedAt', label: 'Released' },
  { key: 'pickedAt', label: 'Picked' },
  { key: 'packedAt', label: 'Packed' },
  { key: 'shippedAt', label: 'Shipped' },
];

function useOrderAction(id: string, path: string, success: string) {
  return useAction(
    () => api<OrderDetailDto>(`/orders/${id}/${path}`, { method: 'POST', body: {} }),
    {
      invalidate: [['orders'], ['inventory']],
      success,
    },
  );
}

export function OrderDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ['orders', 'detail', id],
    queryFn: () => api<OrderDetailDto>(`/orders/${id}`),
    refetchInterval: 20_000,
  });
  const [dialog, setDialog] = useState<
    'hold' | 'cancel' | 'release' | 'ship' | 'slip' | 'bol' | null
  >(null);
  const allocate = useOrderAction(id, 'allocate', 'Allocation run');
  const deallocate = useOrderAction(id, 'deallocate', 'Allocations released to stock');
  const releaseHold = useOrderAction(id, 'release-hold', 'Hold released');
  const o = q.data;
  if (q.isLoading) return <Skeleton height={400} />;
  if (!o) return <ErrorMessage error={q.error} />;
  const manage = can('outbound:manage');

  return (
    <div className="stack-lg">
      <div>
        <Button
          variant="ghost"
          size="sm"
          icon={ArrowLeft}
          onClick={() => void navigate('/outbound/orders')}
        >
          Orders
        </Button>
      </div>
      <PageHeader
        title={`Order ${o.number}`}
        description={
          <span className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <OrderStatusBadge status={o.status} />
            <RiskBadge o={o} />
            <span>{o.clientName}</span>
            <Badge>{o.type === 'b2b' ? 'B2B' : o.type === 'parcel' ? 'Parcel' : o.type}</Badge>
            <span className="muted">Cut-off {cutoffLabel(o.cutoffAt)}</span>
            {o.mustArriveBy ? (
              <span className="muted">MABD {formatDate(`${o.mustArriveBy}T00:00:00`)}</span>
            ) : null}
          </span>
        }
        actions={
          <>
            {manage && ['open', 'backordered'].includes(o.status) ? (
              <Button
                variant="primary"
                icon={Boxes}
                loading={allocate.isPending}
                onClick={() => allocate.mutate(undefined)}
              >
                Allocate
              </Button>
            ) : null}
            {manage && o.status === 'allocated' && !o.waveNumber ? (
              <Button variant="primary" icon={Play} onClick={() => setDialog('release')}>
                Release to picking
              </Button>
            ) : null}
            {manage && o.status === 'allocated' && !o.waveNumber ? (
              <Button
                icon={Undo2}
                loading={deallocate.isPending}
                onClick={() => deallocate.mutate(undefined)}
              >
                Un-allocate
              </Button>
            ) : null}
            {can('outbound:pack') && ['picking', 'picked'].includes(o.status) ? (
              <Button
                variant="primary"
                icon={PackageCheck}
                onClick={() => void navigate(`/outbound/packing?order=${o.id}`)}
              >
                Pack
              </Button>
            ) : null}
            {can('outbound:ship') && o.status === 'packed' ? (
              <Button variant="primary" icon={Send} onClick={() => setDialog('ship')}>
                Ship confirm
              </Button>
            ) : null}
            {manage && o.status === 'on_hold' ? (
              <Button
                icon={PlayCircle}
                loading={releaseHold.isPending}
                onClick={() => releaseHold.mutate(undefined)}
              >
                Release hold
              </Button>
            ) : null}
            {manage && ['open', 'allocated', 'backordered'].includes(o.status) ? (
              <Button icon={PauseCircle} onClick={() => setDialog('hold')}>
                Hold
              </Button>
            ) : null}
            {o.cartons.length ? (
              <Button icon={FileText} onClick={() => setDialog('slip')}>
                Packing list
              </Button>
            ) : null}
            {o.type === 'b2b' && ['packed', 'shipped'].includes(o.status) ? (
              <Button icon={FileText} onClick={() => setDialog('bol')}>
                Bill of lading
              </Button>
            ) : null}
            {manage &&
            ['open', 'on_hold', 'backordered', 'allocated', 'released'].includes(o.status) ? (
              <Button variant="danger" icon={Ban} onClick={() => setDialog('cancel')}>
                Cancel
              </Button>
            ) : null}
          </>
        }
      />
      <Card flush>
        <div className="card-body">
          <div className="steps">
            {STEPS.map((s) => {
              const at = o[s.key] as string | null;
              return (
                <div key={s.label} className={`step ${at ? 'done' : ''}`}>
                  <span className="step-dot">{at ? <CheckCircle2 aria-hidden /> : null}</span>
                  <div>
                    <div className="step-label">{s.label}</div>
                    <div className="step-time">{at ? formatDateTime(at) : '—'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
      {o.status === 'on_hold' && o.holdReason ? (
        <Callout tone="danger" title="On hold">
          {o.holdReason}
        </Callout>
      ) : null}
      {o.late || o.atRisk ? (
        <Callout tone={o.late ? 'danger' : 'warn'} title={o.late ? 'Late' : 'At risk'}>
          {o.riskReason}
        </Callout>
      ) : null}
      {o.warnings.length ? (
        <Callout tone="warn" title="Warnings">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {o.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
      {o.backorderOf ? (
        <Callout tone="info">
          Backorder of{' '}
          <button
            type="button"
            className="btn-link"
            onClick={() => void navigate(`/outbound/orders/${o.backorderOf!.id}`)}
          >
            {o.backorderOf.number}
          </button>
        </Callout>
      ) : null}
      {o.backorders.length ? (
        <Callout tone="info" title="Backorders">
          {o.backorders.map((b) => (
            <button
              key={b.id}
              type="button"
              className="btn-link"
              style={{ marginRight: 12 }}
              onClick={() => void navigate(`/outbound/orders/${b.id}`)}
            >
              {b.number} ({b.status})
            </button>
          ))}
        </Callout>
      ) : null}
      <Card title="Lines" flush>
        <LinesTable lines={o.lineItems} />
      </Card>
      <div className="grid-halves">
        <Card title="Ship to">
          <KeyValue
            items={[
              ['Name', o.shipTo?.name ?? o.shipToName ?? '—'],
              [
                'Address',
                o.shipTo
                  ? [
                      o.shipTo.line1,
                      o.shipTo.line2,
                      `${o.shipTo.city}${o.shipTo.region ? `, ${o.shipTo.region}` : ''} ${o.shipTo.postalCode}`,
                      o.shipTo.country,
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : (o.shipToCity ?? '—'),
              ],
              ['Retailer', o.retailerName ?? '—'],
              ['Carrier', `${o.carrierName ?? '—'}${o.serviceLevel ? ` · ${o.serviceLevel}` : ''}`],
              ['Customer PO', o.poNumber ?? '—'],
              ['Tracking', o.trackingNumbers.length ? o.trackingNumbers.join(', ') : '—'],
              ['BOL', o.bolNumber ?? '—'],
            ]}
          />
        </Card>
        <Card title="Reserved stock" sub="Where each line is allocated, picked and packed" flush>
          {o.allocations.length ? (
            <div className="list">
              {o.allocations.map((a) => (
                <div key={a.id} className="list-item">
                  <div className="grow">
                    <div className="title">
                      <span className="mono">{a.sku}</span> × {formatNumber(a.qty)} at{' '}
                      <span className="mono">{a.locationCode}</span>
                      {a.lpnCode ? <span className="muted"> · {a.lpnCode}</span> : null}
                    </div>
                    <div className="muted small">
                      {a.lot ? `Lot ${a.lot}` : 'No lot'}
                      {a.expiry ? ` · exp ${a.expiry}` : ''}
                    </div>
                  </div>
                  <Badge
                    tone={
                      a.state === 'shipped' ? 'ok' : a.state === 'allocated' ? 'info' : 'accent'
                    }
                  >
                    {a.state}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="card-body muted small">Nothing reserved.</div>
          )}
        </Card>
      </div>
      {o.picks.length ? (
        <Card title="Picks" flush>
          <PicksTable picks={o.picks} />
        </Card>
      ) : null}
      {o.cartons.length ? (
        <Card title="Cartons and pallets" sub="Each carton carries a GS1-128 SSCC label" flush>
          <CartonsTable cartons={o.cartons} />
        </Card>
      ) : null}
      {dialog === 'hold' || dialog === 'cancel' ? (
        <ReasonDialog o={o} kind={dialog} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'release' ? <ReleaseDialog o={o} onClose={() => setDialog(null)} /> : null}
      {dialog === 'ship' ? <ShipDialog o={o} onClose={() => setDialog(null)} /> : null}
      {dialog === 'slip' ? <PackingList o={o} onClose={() => setDialog(null)} /> : null}
      {dialog === 'bol' ? <BillOfLading o={o} onClose={() => setDialog(null)} /> : null}
    </div>
  );
}

function LinesTable({ lines }: { lines: OrderLineDto[] }) {
  const columns: Column<OrderLineDto>[] = [
    {
      id: 'no',
      header: '#',
      width: 40,
      cell: (l) => <span className="num muted">{l.lineNo}</span>,
    },
    {
      id: 'item',
      header: 'Item',
      cell: (l) => (
        <div>
          <div className="primary-cell">{l.description}</div>
          <div className="sub">
            <span className="mono">{l.sku}</span>
            {l.lot ? ` · lot ${l.lot}` : ''}
            {l.minShelfLifeDays ? ` · ≥${l.minShelfLifeDays} d shelf life` : ''}
          </div>
        </div>
      ),
    },
    {
      id: 'qty',
      header: 'Ordered',
      align: 'right',
      cell: (l) => <span className="num">{formatNumber(l.qty)}</span>,
    },
    {
      id: 'alloc',
      header: 'Allocated',
      cell: (l) => <Progress value={l.allocatedQty} total={l.qty} />,
    },
    { id: 'pick', header: 'Picked', cell: (l) => <Progress value={l.pickedQty} total={l.qty} /> },
    {
      id: 'pack',
      header: 'Packed',
      align: 'right',
      cell: (l) => <span className="num">{formatNumber(l.packedQty)}</span>,
    },
    {
      id: 'ship',
      header: 'Shipped',
      align: 'right',
      cell: (l) => <span className="num">{formatNumber(l.shippedQty)}</span>,
    },
    {
      id: 'short',
      header: 'Short',
      align: 'right',
      cell: (l) =>
        l.shortQty ? <Badge tone="warn">{l.shortQty}</Badge> : <span className="faint">0</span>,
    },
  ];
  return <DataTable columns={columns} rows={lines} rowKey={(l) => l.id} pageSize={200} />;
}

export function PicksTable({
  picks,
  onConfirm,
}: {
  picks: PickTaskDto[];
  onConfirm?: (p: PickTaskDto) => void;
}) {
  const columns: Column<PickTaskDto>[] = [
    {
      id: 'seq',
      header: 'Seq',
      width: 50,
      cell: (p) => <span className="num muted">{p.sequence}</span>,
    },
    {
      id: 'loc',
      header: 'Location',
      cell: (p) => (
        <div>
          <span className="mono" style={{ fontWeight: 600 }}>
            {p.locationCode}
          </span>
          <div className="sub">
            Zone {p.zone}
            {p.lpnCode ? ` · ${p.lpnCode}` : ''}
          </div>
        </div>
      ),
    },
    {
      id: 'item',
      header: 'Item',
      cell: (p) => (
        <div>
          <div className="primary-cell mono">{p.sku}</div>
          <div className="sub">
            {p.lot ? `Lot ${p.lot}` : p.description}
            {p.expiry ? ` · exp ${p.expiry}` : ''}
          </div>
        </div>
      ),
    },
    {
      id: 'qty',
      header: 'Qty',
      align: 'right',
      cell: (p) => (
        <span className="num" style={{ fontWeight: 600 }}>
          {formatNumber(p.qty)}
        </span>
      ),
    },
    {
      id: 'order',
      header: 'Order',
      cell: (p) => (
        <div>
          <span className="mono">{p.orderNumber}</span>
          {p.slot ? <div className="sub">Tote slot {p.slot}</div> : null}
        </div>
      ),
    },
    {
      id: 'state',
      header: 'Status',
      cell: (p) =>
        p.state === 'open' ? (
          <Badge tone="info">Open</Badge>
        ) : p.state === 'done' ? (
          <div className="small">
            <Badge tone={p.shortQty ? 'warn' : 'ok'}>
              {p.shortQty ? `Short ${p.shortQty}` : 'Picked'}
            </Badge>
            <div className="sub">
              {p.pickedBy} · {timeAgo(p.pickedAt)}
              {p.toteCode ? ` → ${p.toteCode}` : ''}
            </div>
          </div>
        ) : (
          <Badge tone={p.state === 'short' ? 'danger' : 'neutral'}>
            {p.state === 'short' ? 'Not found' : 'Cancelled'}
          </Badge>
        ),
    },
    ...(onConfirm
      ? [
          {
            id: 'act',
            header: '',
            align: 'right' as const,
            cell: (p: PickTaskDto) =>
              p.state === 'open' ? (
                <Button size="sm" variant="primary" onClick={() => onConfirm(p)}>
                  Pick
                </Button>
              ) : null,
          },
        ]
      : []),
  ];
  return (
    <DataTable
      columns={columns}
      rows={picks}
      rowKey={(p) => p.id}
      pageSize={200}
      empty={
        <EmptyState icon={Boxes} title="No picks">
          Release the order to create picks.
        </EmptyState>
      }
    />
  );
}

export function CartonsTable({ cartons }: { cartons: CartonDto[] }) {
  const columns: Column<CartonDto>[] = [
    {
      id: 'sscc',
      header: 'SSCC',
      cell: (c) => (
        <div>
          <span className="mono">{c.sscc}</span>
          {c.palletSscc ? <div className="sub">On pallet {c.palletSscc}</div> : null}
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (c) => (c.cartonType === 'pallet' ? 'Pallet' : `Carton ${c.cartonType}`),
    },
    {
      id: 'contents',
      header: 'Contents',
      cell: (c) => (
        <span className="small">
          {c.contents.map((x) => `${x.sku} × ${x.qty}${x.lot ? ` (${x.lot})` : ''}`).join(', ')}
        </span>
      ),
    },
    {
      id: 'weight',
      header: 'Weight',
      align: 'right',
      cell: (c) =>
        c.weightG ? `${(c.weightG / 1000).toFixed(2)} kg` : <span className="faint">—</span>,
    },
    {
      id: 'state',
      header: 'Status',
      cell: (c) => (
        <Badge tone={c.state === 'open' ? 'info' : 'ok'}>
          {c.state === 'open' ? 'Open' : c.state === 'closed' ? 'Closed' : 'Shipped'}
        </Badge>
      ),
    },
  ];
  return <DataTable columns={columns} rows={cartons} rowKey={(c) => c.id} pageSize={100} />;
}

function ReasonDialog({
  o,
  kind,
  onClose,
}: {
  o: OrderDetailDto;
  kind: 'hold' | 'cancel';
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const go = useAction(() => api(`/orders/${o.id}/${kind}`, { method: 'POST', body: { reason } }), {
    invalidate: [['orders'], ['inventory']],
    success: kind === 'hold' ? 'Order on hold' : 'Order cancelled; reserved stock released',
    onSuccess: onClose,
  });
  return (
    <Dialog
      title={kind === 'hold' ? `Hold ${o.number}` : `Cancel ${o.number}`}
      description={
        kind === 'hold'
          ? 'A held order keeps its reservations but cannot be released.'
          : 'Reserved stock goes back to available. The client is notified.'
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Back</Button>
          <Button
            variant={kind === 'cancel' ? 'danger-solid' : 'primary'}
            loading={go.isPending}
            disabled={!reason.trim()}
            onClick={() => go.mutate(undefined)}
          >
            {kind === 'hold' ? 'Put on hold' : 'Cancel order'}
          </Button>
        </>
      }
    >
      <Field label="Reason">
        <input
          className="input"
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
    </Dialog>
  );
}

function ReleaseDialog({ o, onClose }: { o: OrderDetailDto; onClose: () => void }) {
  const [method, setMethod] = useState<PickMethod>('discrete');
  const go = useAction(
    () => api(`/orders/${o.id}/release`, { method: 'POST', body: { pickMethod: method } }),
    {
      invalidate: [['orders'], ['picking']],
      success: 'Released to the floor',
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      title={`Release ${o.number}`}
      description="Creates pick tasks sequenced by location pick sequence. To pick several orders together, plan a wave instead."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={Play}
            loading={go.isPending}
            onClick={() => go.mutate(undefined)}
          >
            Release
          </Button>
        </>
      }
    >
      <Field label="Pick method">
        <select
          className="select"
          value={method}
          onChange={(e) => setMethod(e.target.value as PickMethod)}
        >
          <option value="discrete">Discrete — one picker, this order</option>
          <option value="zone">Zone — one list per zone (pick and pass)</option>
        </select>
      </Field>
    </Dialog>
  );
}

function ShipDialog({ o, onClose }: { o: OrderDetailDto; onClose: () => void }) {
  const [tracking, setTracking] = useState('');
  const [bol, setBol] = useState('');
  const [seal, setSeal] = useState('');
  const go = useAction(
    () =>
      api<OrderDetailDto>(`/orders/${o.id}/ship`, {
        method: 'POST',
        body: { trackingNumber: tracking || null, bolNumber: bol || null, sealNo: seal || null },
      }),
    {
      invalidate: [['orders'], ['inventory']],
      success: (r) => `Shipped ${r.number}: confirmation (945/856) sent to the client`,
      onSuccess: onClose,
    },
  );
  const units = o.cartons.reduce((a, c) => a + c.units, 0);
  return (
    <Dialog
      title={`Ship ${o.number}`}
      description={`${o.cartons.length} carton(s) / pallet(s), ${formatNumber(units)} units. Stock leaves the building, the client gets the ship confirmation and charges post to billing.`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            icon={Send}
            loading={go.isPending}
            onClick={() => go.mutate(undefined)}
          >
            Confirm shipment
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Tracking / PRO number">
          <input
            className="input mono"
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
          />
        </Field>
        {o.type === 'b2b' ? (
          <>
            <Field label="BOL number" hint="Blank = generated">
              <input className="input mono" value={bol} onChange={(e) => setBol(e.target.value)} />
            </Field>
            <Field label="Trailer seal">
              <input
                className="input mono"
                value={seal}
                onChange={(e) => setSeal(e.target.value)}
              />
            </Field>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

function DocDialog({
  title,
  id,
  children,
  onClose,
}: {
  title: string;
  id: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <Dialog
      wide
      title={title}
      onClose={onClose}
      footer={
        <Button variant="primary" icon={Printer} onClick={() => printElement(id, title)}>
          Print
        </Button>
      }
    >
      <div id={id}>
        <div className="doc">{children}</div>
      </div>
    </Dialog>
  );
}

function PackingList({ o, onClose }: { o: OrderDetailDto; onClose: () => void }) {
  return (
    <DocDialog title={`Packing list ${o.number}`} id="packing-list" onClose={onClose}>
      <h2 style={{ margin: 0 }}>Packing list</h2>
      <p>
        Order <strong>{o.number}</strong>
        {o.poNumber ? ` · PO ${o.poNumber}` : ''} · {o.clientName}
      </p>
      <p>
        Ship to:{' '}
        {o.shipTo
          ? `${o.shipTo.name}, ${o.shipTo.line1}, ${o.shipTo.city} ${o.shipTo.region ?? ''} ${o.shipTo.postalCode}`
          : o.shipToName}
      </p>
      <table className="doc-table">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Description</th>
            <th>Ordered</th>
            <th>Shipped</th>
          </tr>
        </thead>
        <tbody>
          {o.lineItems.map((l) => (
            <tr key={l.id}>
              <td>{l.sku}</td>
              <td>{l.description}</td>
              <td>{l.qty}</td>
              <td>{o.status === 'shipped' ? l.shippedQty : l.packedQty}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {o.cartons.map((c) => (
        <div key={c.id} style={{ marginTop: 12 }}>
          <Barcode value={c.sscc} height={44} module={1} />
          <div className="small">
            {c.contents.map((x) => `${x.sku} × ${x.qty}${x.lot ? ` lot ${x.lot}` : ''}`).join(', ')}
          </div>
        </div>
      ))}
      {o.giftMessage ? (
        <p style={{ marginTop: 16 }}>
          <em>{o.giftMessage}</em>
        </p>
      ) : null}
    </DocDialog>
  );
}

function BillOfLading({ o, onClose }: { o: OrderDetailDto; onClose: () => void }) {
  const pallets = new Set(
    o.cartons
      .map((c) => c.palletSscc ?? (c.cartonType === 'pallet' ? c.sscc : null))
      .filter(Boolean),
  ).size;
  const weight = o.cartons.reduce((a, c) => a + (c.weightG ?? 0), 0);
  return (
    <DocDialog title={`Bill of lading ${o.bolNumber ?? o.number}`} id="bol" onClose={onClose}>
      <h2 style={{ margin: 0 }}>Straight bill of lading — short form</h2>
      <p className="small">VICS layout · not negotiable</p>
      <table className="doc-table">
        <tbody>
          <tr>
            <th>BOL number</th>
            <td>{o.bolNumber ?? '(assigned at ship confirm)'}</td>
            <th>Date</th>
            <td>{formatDate(o.shippedAt ?? new Date().toISOString())}</td>
          </tr>
          <tr>
            <th>Ship from</th>
            <td>{o.clientName} c/o warehouse</td>
            <th>Carrier</th>
            <td>{o.carrierName ?? '—'}</td>
          </tr>
          <tr>
            <th>Ship to</th>
            <td>
              {o.shipTo
                ? `${o.shipTo.name}, ${o.shipTo.line1}, ${o.shipTo.city} ${o.shipTo.region ?? ''} ${o.shipTo.postalCode}`
                : o.shipToName}
            </td>
            <th>PRO / tracking</th>
            <td>{o.trackingNumbers.join(', ') || '—'}</td>
          </tr>
          <tr>
            <th>Customer PO</th>
            <td>{o.poNumber ?? '—'}</td>
            <th>Must arrive by</th>
            <td>{o.mustArriveBy ?? '—'}</td>
          </tr>
        </tbody>
      </table>
      <table className="doc-table" style={{ marginTop: 12 }}>
        <thead>
          <tr>
            <th>Handling units</th>
            <th>Type</th>
            <th>Packages</th>
            <th>Weight</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{pallets || o.cartons.length}</td>
            <td>{pallets ? 'PLT' : 'CTN'}</td>
            <td>{o.cartons.length}</td>
            <td>{weight ? `${(weight / 453.592).toFixed(0)} lb` : '—'}</td>
            <td>
              {o.lineItems.map((l) => `${l.sku} × ${l.shippedQty || l.packedQty}`).join(', ')}
            </td>
          </tr>
        </tbody>
      </table>
      <p className="small" style={{ marginTop: 16 }}>
        Shipper signature ____________________ Carrier signature ____________________ Date ________
      </p>
    </DocDialog>
  );
}
